import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Send,
  RefreshCw,
  Clock,
  AlertCircle,
  CheckCheck,
  Check,
  MessageSquare,
  Search,
  User,
  Lock,
  Info,
  Ban,
  ShieldCheck,
  RotateCcw,
  UserPlus,
  CalendarPlus,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { messagesApi, leadsApi } from '../services/api';
import { useLanguage } from '../hooks/useLanguage';
import './ChatsPage.css';

const ChatsPage = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);

  // Prevent outer page scroll while chats page is active
  useEffect(() => {
    const mainContent = document.querySelector('.main-content');
    const body = document.body;
    const html = document.documentElement;
    if (mainContent) {
      mainContent.style.overflow = 'hidden';
      mainContent.style.height = `calc(100vh - var(--header-height))`;
      mainContent.style.minHeight = 'unset';
      mainContent.style.padding = '0';
    }
    // Lock body/html scroll entirely on chats page
    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      if (mainContent) {
        mainContent.style.overflow = '';
        mainContent.style.height = '';
        mainContent.style.minHeight = '';
        mainContent.style.padding = '';
      }
      body.style.overflow = '';
      html.style.overflow = '';
    };
  }, []);

  // State
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [contactAction, setContactAction] = useState('');
  const [addingLead, setAddingLead] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Check if within 24-hour window
  const canSend = useMemo(() => {
    if (messages.length === 0) return false;

    const lastInbound = [...messages]
      .filter(m => m.direction === 'inbound')
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))[0];

    if (!lastInbound) return false;

    const lastMessageTime = lastInbound.timestamp || lastInbound.created_at;
    if (!lastMessageTime) return false;

    const messageDate = new Date(typeof lastMessageTime === 'number' ? lastMessageTime : lastMessageTime);
    const now = new Date();
    const hoursDiff = (now - messageDate) / (1000 * 60 * 60);

    return hoursDiff <= 24;
  }, [messages]);

  // Fetch chats list
  const fetchChats = useCallback(async () => {
    setError(null);
    try {
      const chatsRes = await messagesApi.listChats(100, 1);
      if (chatsRes.success) {
        const chatsList = chatsRes.data?.chats || [];
        setChats(chatsList);
        setSelectedChat((current) => (
          current ? chatsList.find((chat) => chat.id === current.id) || current : current
        ));
        if (chatsList.length === 0) setError('noChats');
      } else {
        setError(chatsRes.error || 'Failed to load chats');
        toast.error(chatsRes.error || t('common.error'));
      }
    } catch (err) {
      if (err.message?.includes('Chakra')) {
        setError('chakraOnly');
      } else {
        setError(err.message || 'Failed to load chats');
        toast.error(t('common.error'));
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [t]);

  // Fetch messages for selected chat
  const fetchMessages = useCallback(async (chatId, silent = false) => {
    if (!chatId) return;
    if (!silent) setLoadingMessages(true);
    try {
      const msgRes = await messagesApi.listByChat(chatId, 200, 1);
      if (msgRes.success) {
        const sortedMessages = (msgRes.data?.messages || []).sort(
          (a, b) => (a.timestamp || a.created_at || 0) - (b.timestamp || b.created_at || 0)
        );
        setMessages(sortedMessages);
      } else {
        toast.error(msgRes.error || t('common.error'));
      }
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  }, [t]);

  // Initial load
  useEffect(() => { fetchChats(); }, [fetchChats]);

  // Fetch messages when chat is selected
  useEffect(() => {
    if (selectedChat?.id) {
      fetchMessages(selectedChat.id);
    }
  }, [selectedChat?.id, fetchMessages]);

  // Chakra is the source of truth. Poll it so new chats, names, and messages
  // appear without requiring a page reload.
  useEffect(() => {
    const interval = window.setInterval(() => {
      fetchChats();
      if (selectedChat?.id) fetchMessages(selectedChat.id, true);
    }, 10000);
    return () => window.clearInterval(interval);
  }, [fetchChats, fetchMessages, selectedChat?.id]);

  // Scroll to bottom when messages change — scroll ONLY the messages container
  useEffect(() => {
    const area = messagesAreaRef.current;
    if (area) {
      area.scrollTop = area.scrollHeight;
    }
  }, [messages]);

  // Focus input when chat selected and messages loaded
  useEffect(() => {
    if (selectedChat && !loadingMessages && canSend) {
      inputRef.current?.focus();
    }
  }, [selectedChat, loadingMessages, canSend]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchChats();
    if (selectedChat) {
      fetchMessages(selectedChat.id);
    }
  };

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    setMessages([]);
    setNewMessage('');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat || !canSend) return;

    setSending(true);
    try {
      const result = await messagesApi.send(selectedChat.phone_number, newMessage.trim());
      if (result.success) {
        const sentMessage = {
          id: `local-${Date.now()}`,
          text: newMessage.trim(),
          direction: 'outbound',
          timestamp: Date.now(),
          delivery_status: 'SENT',
        };
        setMessages(prev => [...prev, sentMessage]);
        setNewMessage('');
        setTimeout(() => fetchMessages(selectedChat.id), 2000);
      } else {
        toast.error(result.error || t('chats.sendFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('chats.sendFailed'));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleContactAction = async (action) => {
    if (!selectedChat?.phone_number || contactAction) return;
    if (action === 'restart' && !window.confirm(t('chats.restartConfirm'))) return;
    setContactAction(action);
    try {
      const result = await messagesApi.contactAction(selectedChat.phone_number, action);
      if (!result.success) throw new Error(result.error || t('common.error'));
      toast.success(t(`chats.${action}Success`));
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setContactAction('');
    }
  };

  const handleAddLead = async () => {
    if (!selectedChat?.phone_number || addingLead) return;
    setAddingLead(true);
    try {
      const result = await leadsApi.createFromChat({
        phone: selectedChat.phone_number,
        name: selectedChat.contact_name || '',
        chatId: selectedChat.id,
      });
      if (!result.success) throw new Error(result.error || t('common.error'));
      toast.success(result.created ? t('chats.leadAdded') : t('chats.leadExists'));
    } catch (err) {
      toast.error(err.message || t('common.error'));
    } finally {
      setAddingLead(false);
    }
  };

  const handleAddAppointment = () => {
    if (!selectedChat) return;
    navigate('/appointments', {
      state: {
        prefillCustomer: {
          name: selectedChat.contact_name || '',
          phone: selectedChat.phone_number || '',
        },
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // --- Formatting helpers ---
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(typeof timestamp === 'number' ? timestamp : timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(typeof timestamp === 'number' ? timestamp : timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return t('common.today');
    if (date.toDateString() === yesterday.toDateString()) return t('common.yesterday');
    return date.toLocaleDateString();
  };

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(typeof timestamp === 'number' ? timestamp : timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return formatTime(timestamp);
    if (date.toDateString() === yesterday.toDateString()) return t('common.yesterday');
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Group messages by date
  const groupedMessages = useMemo(() => {
    return messages.reduce((groups, message) => {
      const timestamp = message.timestamp || message.created_at;
      const dateKey = timestamp ? formatDate(timestamp) : t('common.unknown');
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(message);
      return groups;
    }, {});
  }, [messages, t]);

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'DELIVERED':
        return <CheckCheck size={14} className="wa-status-delivered" />;
      case 'READ':
        return <CheckCheck size={14} className="wa-status-read" />;
      case 'SENT':
        return <Check size={14} className="wa-status-sent" />;
      case 'FAILED':
        return <AlertCircle size={14} className="wa-status-failed" />;
      default:
        return <Clock size={14} className="wa-status-pending" />;
    }
  };

  const getChatDisplayName = (chat) => {
    return chat.contact_name || chat.phone_number || t('common.unknown');
  };

  // Render an inline media attachment (image/video/audio/document) for a message.
  const renderMedia = (msg) => {
    const type = (msg.data_type || '').toLowerCase();
    if (!type || type === 'text') return null;
    const src = msg.media_id ? messagesApi.mediaUrl(msg.media_id) : '';
    if (!src) {
      return <p className="wa-msg-text">{`📎 ${msg.data_type}`}</p>;
    }
    if (type === 'image' || type === 'sticker') {
      return (
        <a href={src} target="_blank" rel="noreferrer" className="wa-msg-media">
          <img src={src} alt={t('chats.image', 'Image')} loading="lazy" />
        </a>
      );
    }
    if (type === 'video') {
      return <video className="wa-msg-media" src={src} controls preload="metadata" />;
    }
    if (type === 'audio' || type === 'voice') {
      return <audio className="wa-msg-audio" src={src} controls preload="none" />;
    }
    // document / other → download link
    return (
      <a href={src} target="_blank" rel="noreferrer" className="wa-msg-doc">
        <Info size={14} />
        <span>{t('chats.openDocument', 'Open document')}</span>
      </a>
    );
  };

  // Filter chats by search
  const filteredChats = useMemo(() => {
    if (!searchQuery.trim()) return chats;
    const q = searchQuery.toLowerCase();
    return chats.filter(chat =>
      (chat.contact_name || '').toLowerCase().includes(q) ||
      (chat.phone_number || '').includes(q) ||
      (chat.last_message || '').toLowerCase().includes(q)
    );
  }, [chats, searchQuery]);

  // --- Loading state ---
  if (loading) {
    return (
      <div className="wa-loading-screen">
        <div className="loading-spinner"></div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className={`wa-container ${isRTL ? 'wa-rtl' : ''}`}>
      {/* ========= LEFT PANEL: Chat List ========= */}
      <aside className={`wa-sidebar ${selectedChat ? 'wa-sidebar-hidden-mobile' : ''}`}>
        {/* Sidebar header */}
        <div className="wa-sidebar-header">
          <h2 className="wa-sidebar-title">{t('chats.title')}</h2>
          <button
            className="wa-icon-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            title={t('common.refresh')}
          >
            <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          </button>
        </div>

        {/* Search */}
        <div className="wa-search-bar">
          <Search size={16} className="wa-search-icon" />
          <input
            type="text"
            className="wa-search-input"
            placeholder={t('common.search') + '...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Chats list */}
        <div className="wa-chat-list">
          {error === 'chakraOnly' ? (
            <div className="wa-empty-state">
              <AlertCircle size={40} />
              <p>{t('chats.chakraOnly')}</p>
            </div>
          ) : filteredChats.length === 0 ? (
            <div className="wa-empty-state">
              <MessageSquare size={40} />
              <p>{searchQuery ? t('common.noData') : t('chats.noChats')}</p>
            </div>
          ) : (
            filteredChats.map((chat) => (
              <div
                key={chat.id}
                className={`wa-chat-item ${selectedChat?.id === chat.id ? 'wa-chat-item-active' : ''}`}
                onClick={() => handleSelectChat(chat)}
              >
                <div className="wa-avatar">
                  {chat.contact_photo ? <img src={chat.contact_photo} alt="" /> : <User size={22} />}
                </div>
                <div className="wa-chat-item-body">
                  <div className="wa-chat-item-top">
                    <span className="wa-chat-item-name">{getChatDisplayName(chat)}</span>
                    <span className="wa-chat-item-time">{formatLastMessageTime(chat.last_message_time)}</span>
                  </div>
                  <div className="wa-chat-item-bottom">
                    <span className="wa-chat-item-preview">
                      {chat.last_message_direction === 'outbound' && <Check size={14} className="wa-preview-check" />}
                      {chat.last_message || t('chats.noMessages')}
                    </span>
                    {chat.unread_count > 0 && (
                      <span className="wa-unread-badge">{chat.unread_count}</span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* ========= RIGHT PANEL: Conversation ========= */}
      <main className={`wa-main ${selectedChat ? 'wa-main-visible-mobile' : ''}`}>
        {!selectedChat ? (
          /* Empty state — no chat selected */
          <div className="wa-no-chat-selected">
            <div className="wa-no-chat-icon">
              <MessageSquare size={64} />
            </div>
            <h3>{t('chats.title')}</h3>
            <p>{t('chats.subtitle')}</p>
          </div>
        ) : (
          <>
            {/* Conversation header */}
            <div className="wa-conv-header">
              {/* Mobile back button */}
              <button className="wa-back-btn" onClick={() => { setSelectedChat(null); setMessages([]); }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points={isRTL ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
                </svg>
              </button>
              <div className="wa-avatar wa-avatar-sm">
                {selectedChat.contact_photo ? <img src={selectedChat.contact_photo} alt="" /> : <User size={18} />}
              </div>
              <div className="wa-conv-header-info">
                <span className="wa-conv-header-name">{getChatDisplayName(selectedChat)}</span>
                {selectedChat.phone_number && selectedChat.contact_name && (
                  <span className="wa-conv-header-phone">{selectedChat.phone_number}</span>
                )}
              </div>
              <button
                className="wa-icon-btn"
                onClick={() => fetchMessages(selectedChat.id)}
                title={t('common.refresh')}
              >
                <RefreshCw size={16} />
              </button>
            </div>

            {/* Contact action bar */}
            <div className="wa-action-bar">
              <button className="wa-action-btn wa-action-lead" disabled={addingLead} onClick={handleAddLead}>
                <UserPlus size={15} />
                <span>{t('chats.addLead')}</span>
              </button>
              <button className="wa-action-btn wa-action-appointment" onClick={handleAddAppointment}>
                <CalendarPlus size={15} />
                <span>{t('chats.addAppointment')}</span>
              </button>
              <span className="wa-action-divider" />
              <button className="wa-action-btn wa-action-block" disabled={!!contactAction} onClick={() => handleContactAction('block')}>
                <Ban size={15} />
                <span>{t('chats.blockNumber')}</span>
              </button>
              <button className="wa-action-btn wa-action-whitelist" disabled={!!contactAction} onClick={() => handleContactAction('whitelist')}>
                <ShieldCheck size={15} />
                <span>{t('chats.whitelistNumber')}</span>
              </button>
              <button className="wa-action-btn" disabled={!!contactAction} onClick={() => handleContactAction('restart')}>
                <RotateCcw size={15} className={contactAction === 'restart' ? 'spin' : ''} />
                <span>{t('chats.restartBot')}</span>
              </button>
            </div>

            {/* Messages area */}
            <div className="wa-messages-area" ref={messagesAreaRef}>
              {loadingMessages ? (
                <div className="wa-messages-loading">
                  <div className="loading-spinner"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="wa-messages-empty">
                  <MessageSquare size={48} />
                  <p>{t('chats.noMessages')}</p>
                </div>
              ) : (
                Object.entries(groupedMessages).map(([date, dateMessages]) => (
                  <div key={date} className="wa-msg-group">
                    <div className="wa-date-pill">
                      <span>{date}</span>
                    </div>
                    {dateMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`wa-msg ${msg.direction === 'inbound' ? 'wa-msg-in' : 'wa-msg-out'}`}
                      >
                        <div className="wa-msg-bubble">
                          {renderMedia(msg)}
                          {msg.text && <p className="wa-msg-text">{msg.text}</p>}
                          <span className="wa-msg-meta">
                            <span className="wa-msg-time">{formatTime(msg.timestamp || msg.created_at)}</span>
                            {msg.direction === 'outbound' && (
                              <span className="wa-msg-status">{getStatusIcon(msg.delivery_status)}</span>
                            )}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input area */}
            <div className="wa-input-area">
              {!canSend && messages.length > 0 && (
                <div className="wa-window-closed-banner">
                  <Lock size={14} />
                  <div className="wa-window-closed-text">
                    <span className="wa-window-closed-title">{t('chats.windowClosed')}</span>
                    <span className="wa-window-closed-reason">
                      <Info size={12} />
                      {t('chats.windowClosedReason')}
                    </span>
                  </div>
                </div>
              )}
              <form className="wa-input-row" onSubmit={handleSendMessage}>
                <input
                  ref={inputRef}
                  type="text"
                  className="wa-text-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={canSend ? t('chats.typeMessage') : t('chats.cannotSend')}
                  disabled={!canSend || sending}
                />
                <button
                  type="submit"
                  className={`wa-send-btn ${canSend && newMessage.trim() ? 'wa-send-btn-active' : ''}`}
                  disabled={!canSend || !newMessage.trim() || sending}
                >
                  {sending ? (
                    <span className="wa-send-spinner"></span>
                  ) : (
                    <Send size={20} />
                  )}
                </button>
              </form>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default ChatsPage;
